import { Container } from '@tencent/alloy-renderer';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { IBaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { TableCellLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/logic/component-logic/table-cell-logic';
import { TableCellComponentWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/table/table-cell-component-wrapper';
import { getTopContainer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/utils/get-top-container';
import _debounce from 'lodash/debounce';
import { GraphicsRenderComponent } from '@tencent/alloy-graphics';
import { generateGraphicsModel } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';
import { updateWrapperTransform } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/utils/update-wrapper-transform';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { ElementsAnalyze } from '@src/slide/domain/ui-model/element-modeles/elements-analysis';

// 单元格数量上限（超过此数值则启用大表格模式）
const BIG_TABLE_LIMIT = 400;

export class TableCellWrapperLogic extends TableCellLogic {
    declare protected component: TableCellComponentWrapper;
    declare protected componentOptions: InterfaceFactoryOptions<ITableModel>;
    // 单元格临时容器的背景
    private cellNewBackground;
    // 当前单元格在表格处的索引
    private cellOldIndex;
    // 重算表格高度
    private delayResizeDisplayRowHeight = _debounce(() => this.componentOptions.model.resizeDisplayRowHeight());
    // 表格句柄
    private tableComponent;
    // 单元格的原父级
    private cellOldParent;

    // 当前是否为大表格模式
    private bigTableMode = false;
    // 单元格临时容器
    private cellNewParent;
    public constructor(componentOptions: InterfaceFactoryOptions<ITableModel>, component: IBaseComponent) {
        super(componentOptions, component);
        this.component.setEventHandlers({
            handleEditorMouseDown: this.handleEditorMouseDown,
            handleEditorMouseUp: this.handleEditorMouseUp,
            handleTextChange: this.handleTextChange,
            handleEnterEditing: this.handleEnterEditing,
            handleExitEditing: this.handleExitEditing,
            handleChange: this.handleChange,
            beforeEditorValueChange: this.beforeEditorValueChange,
            handleEditorStyleChange: this.handleEditorStyleChange,
            handleSelectionChange: this.handleSelectionChange,
            revertPickCell: this.revertPickCell,
            renderCellBackground: this.renderCellBackground,
            tableOtherPartMakeCache: this.tableOtherPartMakeCache,
            onUpdateComponent: this.onUpdateComponent,
            updateBigTableMode: this.updateBigTableMode,
        });
    }

    protected handleEnterEditing = (): void => {
        if (this.bigTableMode) {
            this.pickCell();
        }
        super.handleEnterEditing();
    };

    protected handleExitEditing = (): void => {
        if (this.bigTableMode) {
            this.revertPickCell();
        }
        super.handleExitEditing();
    };

    protected dealRowHeightChange = (): void => {
        if (this.bigTableMode && this.cellNewParent) {
            // 大表格模式下，当单元格高度发生变化时，先不同步到model，只更新editor和单元格背景。
            // 只有当超过页面边界 || 退出编辑态时才同步至model
            this.component.renderTextEditor();
            this.renderCellBackground();
            const canvasHeight = this.componentOptions?.getApplication?.().height;
            if (canvasHeight && this.cellNewParent.worldBounds.maxY > canvasHeight) {
                this.delayResizeDisplayRowHeight();
            }
        } else {
            super.dealRowHeightChange();
        }
    };

    private onUpdateComponent = () => {
        // 更新当前表格是否为大表格模式
        this.updateBigTableMode();

        // 如果当前表格处于大表格模式，并且当前单元格就是编辑状态，则当外部changeset造成刷新时，此处也要刷新
        if (this.bigTableMode && this.cellNewParent) {
            if (this.componentOptions.isEditable) {
                this.renderCellBackground();
                this.tableOtherPartMakeCache();
            } else {
                // 协同场景退出编辑态
                this.revertPickCell();
            }
        }
    };

    // 将当前单元格独立出去，将表格的其他部分加入缓存
    private pickCell = (): void => {
        const tableComponent = getTopContainer(this.component);
        if (!tableComponent) {
            return;
        }
        // 获取表格挂载的节点
        const elementsLayer = tableComponent.parent;

        // 记录表格、原单元格父节点/索引
        this.tableComponent = tableComponent;
        this.cellOldParent = this.component.parent;
        this.cellOldIndex = this.component.parent?.children.findIndex(e => e === this.component);

        // 创建临时单元格容器
        this.cellNewParent = this.renderCellNewParent();
        // 渲染单元格背景
        this.renderCellBackground();

        // 将当前单元格迁入临时容器
        this.cellNewParent.addChild(this.component);
        elementsLayer?.addChild(this.cellNewParent);

        // 给表格其他部分加入缓存（因为每次pick的单元格可能不一样，所以需要清掉缓存）
        tableComponent.clearCache();
        this.tableOtherPartMakeCache();
    };

    // 将独立的单元格复原
    private revertPickCell = (): void => {
        if (!this.cellNewParent) {
            return;
        }
        this.cellOldParent.addChildAt(this.component, this.cellOldIndex);
        this.cellNewParent.parent?.removeChild(this.cellNewParent);
        this.cellNewParent.dispose();
        this.cellOldParent = null;
        this.cellNewParent = null;
        this.cellNewBackground = null;
        this.cellOldIndex = null;
    };

    // 渲染临时容器背景
    private renderCellBackground = (): void => {
        if (!this.cellNewParent) {
            return;
        }
        const cell = this.component.cellModel;
        const cellStyle = cell.props.style || {};
        const bgModel = {
            ...cell.props.style,
            bgShape: ShapeType.rect,
            editable: false,
            selected: false,
            border: {
                color: '#94BF43',
                style: 'solid',
                width: 4,
            },
        };
        const textDirection = cell?.textBodyStyle?.wrapperStyle?.textDirection;
        bgModel.height = ([
            TextDirectionType.VERTICAL,
            TextDirectionType.ROTATE_90,
            TextDirectionType.ROTATE_270,
            TextDirectionType.WORD_ART_VERTICAL_RTL,
            TextDirectionType.WORD_ART_VERTICAL_LTR,
        ] as any[]).includes(textDirection) ? cellStyle.height : Math.max(cell.displayHeight ?? 0, cellStyle.height);
        const frame = {
            left: cellStyle.left,
            top: cellStyle.top,
            width: cellStyle.width,
            height: bgModel.height,
        };
        const graphicsModel = generateGraphicsModel({
            style: bgModel,
            frame,
            factoryOptions: this.componentOptions,
        });
        if (!this.cellNewBackground) {
            this.cellNewBackground = new GraphicsRenderComponent(graphicsModel);
            this.cellNewParent.addChild(this.cellNewBackground);
        } else {
            this.cellNewBackground.updateModel(graphicsModel);
        }
    };

    // 生成单元格临时容器
    private renderCellNewParent = (): Container | undefined => {
        if (this.cellNewParent) {
            return;
        }
        this.cellNewParent = new Container();
        if (this.cellOldParent) {
            this.cellNewParent.onDoubleClick(e => this.cellOldParent.dispatch(e));
            this.cellNewParent.onMouseDown(e => this.cellOldParent.dispatch(e));
            this.cellNewParent.onMouseMove(e => this.cellOldParent.dispatch(e));
        }
        this.cellNewParent = updateWrapperTransform(this.cellNewParent, this.componentOptions);
        return this.cellNewParent;
    };

    // 表格的其他部分加入缓存
    private tableOtherPartMakeCache = (): void => {
        if (!this.tableComponent) {
            return;
        }
        if (this.tableComponent.cache.isEmpty) {
            this.tableComponent?.makeCache(false, true);
        }
    };

    // 更新当前表格的状态（是否为大表格模式）
    private updateBigTableMode = () => {
        this.bigTableMode = (ElementsAnalyze.includeBaseElementCount(this.componentOptions.model) >= BIG_TABLE_LIMIT)
            && browser.isPc;
    };
}
